/**
 * Re-test detail — full page renderer for the "defend your score"
 * adjudication row.
 *
 * The player-detail modal (`_openRetestDetailModal` in player_detail.js)
 * already renders the same data as an overlay; this page is the deep-
 * link target for `/admin/v2/retests/:id` so the email alert links
 * straight into a routable URL. Everything below re-uses the shared
 * `integrityChips` / `perAnswerSignalsHtml` helpers so the strip reads
 * the same as the duel + live surfaces.
 *
 * Verdict recording (Mark passed / failed / inconclusive) is preserved
 * here — recording the verdict NEVER writes account_state (the backend
 * enforces that). The response's `account_unchanged=true` is surfaced
 * inline in the toast + as a permanent reminder in the verdict area.
 */

import { api, ApiError } from '../../api.js';
import { toast } from '../../components/toast.js';
import {
  adminGetRetest,
  adminReviewRetest,
  formatLagosDeadline,
} from '../../lib/retest.js';
import { escape } from '../../components/form.js';
import { errorState, fmtInt, fmtRelTime } from './common.js';
import { integrityChips, perAnswerSignalsHtml } from './integrity.js';

export async function renderRetestDetail(host, ctx) {
  if (!ctx.retestId) {
    host.innerHTML = errorState('Missing re-test id.');
    return;
  }
  host.innerHTML = `
    <a href="/admin/v2/players/${escape(ctx.playerId || '')}" data-link class="adm2-btn"
       style="display:inline-flex;align-items:center;gap:8px;margin-bottom:18px;text-decoration:none;">
      ‹ Back
    </a>
    <div class="adm2-skl" style="height:220px;width:100%;border-radius:14px;"></div>
  `;
  await _load(host, ctx);
}

async function _load(host, ctx) {
  let d;
  try {
    d = await adminGetRetest(ctx.retestId);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      ctx.navigate('/login');
      return;
    }
    if (err instanceof ApiError && err.status === 404) {
      host.innerHTML = `<div class="adm2-error">Re-test not found.</div>`;
      return;
    }
    host.innerHTML = errorState(err);
    return;
  }
  host.innerHTML = _renderPage(d);
  _wire(host, ctx, d);
}

function _renderPage(d) {
  const agg = d.integrity_aggregates || {};
  const bandChip = d.comparison_band
    ? `<span class="adm2-chip" style="${_bandChipStyle(d.comparison_band)}">${escape(d.comparison_band.replace(/_/g, ' '))}</span>`
    : '';
  const verdict = d.review_outcome
    ? `<span class="adm2-chip" style="background:rgba(139,107,242,.14);color:#B7A5F5;border-color:rgba(139,107,242,.4);">Marked ${escape(d.review_outcome)}</span>`
    : '';
  const backHref = d.user_id ? `/admin/v2/players/${encodeURIComponent(d.user_id)}` : '/admin/v2';
  const acc =
    d.original_accuracy != null && d.retest_accuracy != null
      ? `${(d.original_accuracy * 100).toFixed(0)}% → ${(d.retest_accuracy * 100).toFixed(0)}%`
      : '—';
  const avgMs =
    d.original_avg_ms != null && d.retest_avg_ms != null
      ? `avg ${fmtInt(d.original_avg_ms)}ms → ${fmtInt(d.retest_avg_ms)}ms`
      : '';

  const historyItems = (d.deadline_history || [])
    .map(
      (h) => `
        <div style="font-size:12px;color:var(--a-muted);font-family:'IBM Plex Mono',monospace;">
          extended by ${escape(h.by_admin_id || '?')} · ${escape(formatLagosDeadline(h.new))}
          · ${escape(h.note || '')}
        </div>`,
    )
    .join('');

  const anyBad = agg.any_untrusted || agg.decoy_hit || agg.overlay_detected;
  const integrityWarning = anyBad
    ? `<div style="margin-top:12px;padding:12px 14px;background:rgba(248,113,113,.10);border-left:3px solid #F87171;border-radius:6px;font-size:12.5px;color:var(--a-ink);line-height:1.55;">
         <b style="color:#F87171;">Bot-run adjudication warning.</b>
         A high re-test score achieved with untrusted clicks, a decoy hit, or an
         overlay observed is <em>worse</em> evidence than a low score — it means
         the adjudication quiz itself was botted.
       </div>`
    : '';

  const perAnswer = (d.per_answer || []).map(_perAnswerHtml).join('');
  const originalRows = (d.original_answers || []).map(_originalAnswerHtml).join('');
  const canRecord = (d.status === 'submitted' || d.status === 'expired') && !d.review_outcome;

  return `
    <a href="${escape(backHref)}" data-link class="adm2-btn"
       style="display:inline-flex;align-items:center;gap:8px;margin-bottom:18px;text-decoration:none;">
      ‹ Back to player
    </a>

    <div class="adm2-panel" style="padding:20px;margin-bottom:16px;">
      <div style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-bottom:12px;">
        <span class="adm2-chip" style="${_statusChipStyle(d.status)}">${escape(d.status)}</span>
        ${bandChip}
        ${verdict}
        ${d.user_email ? `<a href="/admin/v2/players/${encodeURIComponent(d.user_id)}" data-link style="color:var(--a-brand);text-decoration:none;">${escape(d.user_email)}</a>` : ''}
        <span style="font:400 12px 'IBM Plex Mono',monospace;color:var(--a-faint);margin-left:auto;">${escape(d.id)}</span>
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:12px;">
        <div style="background:var(--a-panel-2);border:1px solid var(--a-border);border-radius:10px;padding:12px 14px;">
          <div style="font:600 10px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-faint);">Accuracy (original → re-test)</div>
          <div style="font:600 18px 'IBM Plex Mono',monospace;color:var(--a-ink);margin-top:6px;">${escape(acc)}</div>
          ${avgMs ? `<div style="font-size:11px;color:var(--a-muted);margin-top:4px;">${escape(avgMs)}</div>` : ''}
        </div>
        <div style="background:var(--a-panel-2);border:1px solid var(--a-border);border-radius:10px;padding:12px 14px;">
          <div style="font:600 10px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-faint);">Score</div>
          <div style="font:600 18px 'IBM Plex Mono',monospace;color:var(--a-ink);margin-top:6px;">${fmtInt(d.n_correct)}/${fmtInt(d.n_total)}</div>
        </div>
        <div style="background:var(--a-panel-2);border:1px solid var(--a-border);border-radius:10px;padding:12px 14px;">
          <div style="font:600 10px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-faint);">Deadline</div>
          <div style="font-size:13px;color:var(--a-ink);margin-top:6px;">${escape(formatLagosDeadline(d.deadline_at))}</div>
          <div style="font-size:11px;color:var(--a-muted);margin-top:4px;">extended ${escape(String(d.deadline_extended_count || 0))} time(s)</div>
          ${historyItems ? `<div style="margin-top:6px;">${historyItems}</div>` : ''}
        </div>
        <div style="background:var(--a-panel-2);border:1px solid var(--a-border);border-radius:10px;padding:12px 14px;">
          <div style="font:600 10px 'IBM Plex Sans',sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--a-faint);">Submitted</div>
          <div style="font-size:13px;color:var(--a-ink);margin-top:6px;">${escape(fmtRelTime(d.submitted_at) || '—')}</div>
        </div>
      </div>
    </div>

    <div class="adm2-section-label">Client-integrity signals</div>
    <div class="adm2-panel" style="padding:16px;">
      ${integrityChips(d.integrity_aggregates, { compact: false })}
      ${integrityWarning}
    </div>

    <div class="adm2-section-label">Original game — user's answers</div>
    <div class="adm2-panel" style="padding:16px;">
      <div style="display:flex;flex-direction:column;gap:6px;">
        ${originalRows || '<div style="color:var(--a-faint);font-size:12px;">(no per-answer detail available)</div>'}
      </div>
    </div>

    <div class="adm2-section-label">Re-test — user's answers</div>
    <div class="adm2-panel" style="padding:16px;">
      <div style="display:flex;flex-direction:column;gap:6px;">
        ${perAnswer || '<div style="color:var(--a-faint);font-size:12px;">(no per-answer detail available)</div>'}
      </div>
    </div>

    ${canRecord ? _verdictAreaHtml(d) : ''}
  `;
}

function _verdictAreaHtml(d) {
  return `
    <div class="adm2-section-label">Record verdict (advisory — does NOT change account_state)</div>
    <div class="adm2-panel" style="padding:16px;">
      <textarea id="adm2-retest-review-note"
                placeholder="Reasoning + anything to email the user separately…"
                style="width:100%;min-height:70px;padding:9px 10px;font-size:13px;background:var(--a-panel-2);border:1px solid var(--a-border);color:var(--a-ink);border-radius:6px;font-family:inherit;line-height:1.4;resize:vertical;"></textarea>
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:12px;">
        <button type="button" data-verdict="passed"
          style="padding:9px 16px;font-size:13px;font-weight:700;background:rgba(74,222,128,.14);border:1px solid rgba(74,222,128,.4);color:#4ADE80;border-radius:6px;cursor:pointer;">Mark passed</button>
        <button type="button" data-verdict="failed"
          style="padding:9px 16px;font-size:13px;font-weight:700;background:rgba(248,113,113,.14);border:1px solid rgba(248,113,113,.4);color:#F87171;border-radius:6px;cursor:pointer;">Mark failed</button>
        <button type="button" data-verdict="inconclusive"
          style="padding:9px 16px;font-size:13px;font-weight:700;background:rgba(251,191,36,.14);border:1px solid rgba(251,191,36,.4);color:#FBBF24;border-radius:6px;cursor:pointer;">Inconclusive</button>
      </div>
      <div style="margin-top:10px;font-size:11px;color:var(--a-muted);line-height:1.5;">
        Current account state:
        <b style="font-family:'IBM Plex Mono',monospace;color:var(--a-ink);">${escape(d.user_account_state || 'unknown')}</b>.
        Recording a verdict here does NOT unlock or ban — do that separately from
        the account-state control on the
        <a href="/admin/v2/players/${escape(d.user_id)}" data-link style="color:var(--a-brand);">player page</a>.
      </div>
    </div>
  `;
}

function _wire(host, ctx, d) {
  host.querySelectorAll('button[data-verdict]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const outcome = btn.dataset.verdict;
      const noteEl = host.querySelector('#adm2-retest-review-note');
      const note = (noteEl && noteEl.value.trim()) || '';
      btn.disabled = true;
      try {
        const res = await adminReviewRetest(ctx.retestId, {
          review_outcome: outcome,
          note,
        });
        const stateNote = res.account_unchanged
          ? ` Account state (${res.user_account_state}) is unchanged — unlocking is a separate manual step.`
          : '';
        toast.success(`Marked ${outcome}.${stateNote}`);
        // Re-fetch + re-render so the verdict chip appears and the
        // verdict area disappears.
        await _load(host, ctx);
      } catch (err) {
        toast.error(err.message || 'Review failed.');
        btn.disabled = false;
      }
    });
  });
  void api; // silence unused-import lint if api ever falls out
  void d;
}

// ---------------------------------------------------------------------------
// Small style helpers — mirror the modal's palette
// ---------------------------------------------------------------------------

function _statusChipStyle(status) {
  switch (status) {
    case 'sent':
    case 'in_progress':
      return 'background:rgba(251,191,36,.14);color:#FBBF24;border-color:rgba(251,191,36,.4);';
    case 'submitted':
      return 'background:rgba(74,222,128,.14);color:#4ADE80;border-color:rgba(74,222,128,.4);';
    case 'expired':
      return 'background:rgba(248,113,113,.14);color:#F87171;border-color:rgba(248,113,113,.4);';
    default:
      return 'background:var(--a-panel-2);color:var(--a-muted);border-color:var(--a-border);';
  }
}

function _bandChipStyle(band) {
  switch (band) {
    case 'consistent':
      return 'background:rgba(74,222,128,.12);color:#4ADE80;border-color:rgba(74,222,128,.35);';
    case 'moderate_drop':
      return 'background:rgba(251,191,36,.12);color:#FBBF24;border-color:rgba(251,191,36,.35);';
    case 'severe_drop':
      return 'background:rgba(248,113,113,.12);color:#F87171;border-color:rgba(248,113,113,.35);';
    default:
      return 'background:transparent;color:var(--a-muted);border-color:var(--a-border);';
  }
}

function _perAnswerHtml(a) {
  const good = !!a.is_correct;
  const picked = a.picked_index == null ? '—' : String.fromCharCode(65 + Number(a.picked_index));
  const correctL = String.fromCharCode(65 + Number(a.correct_index));
  return `
    <div style="display:flex;align-items:flex-start;gap:10px;padding:9px 12px;background:var(--a-panel-2);border:1px solid var(--a-border);border-radius:8px;">
      <span style="font-family:'IBM Plex Mono',monospace;font-size:11px;color:var(--a-muted);min-width:24px;">Q${a.order + 1}</span>
      <div style="flex:1;min-width:0;">
        <div style="font-size:12.5px;color:var(--a-ink);line-height:1.4;overflow-wrap:anywhere;">${escape(a.prompt || '')}</div>
        <div style="font-size:11px;color:var(--a-muted);margin-top:3px;font-family:'IBM Plex Mono',monospace;">
          picked <b style="color:${good ? '#4ADE80' : '#F87171'};">${escape(picked)}</b>
          · correct <b style="color:#4ADE80;">${escape(correctL)}</b>
          · ${fmtInt(a.ms)}ms
        </div>
        <div style="margin-top:5px;">${perAnswerSignalsHtml(a.signals)}</div>
      </div>
    </div>
  `;
}

function _originalAnswerHtml(a) {
  const picked = a.picked_index == null ? '—' : String.fromCharCode(65 + Number(a.picked_index));
  const correctL = a.correct_index == null ? '—' : String.fromCharCode(65 + Number(a.correct_index));
  const good = !!a.is_correct;
  return `
    <div style="display:flex;align-items:flex-start;gap:10px;padding:9px 12px;background:var(--a-panel-2);border:1px solid var(--a-border);border-radius:8px;">
      <span style="font-family:'IBM Plex Mono',monospace;font-size:11px;color:var(--a-muted);min-width:24px;">Q${a.order + 1}</span>
      <div style="flex:1;min-width:0;">
        <div style="font-size:12.5px;color:var(--a-ink);line-height:1.4;overflow-wrap:anywhere;">${escape(a.prompt || '')}</div>
        <div style="font-size:11px;color:var(--a-muted);margin-top:3px;font-family:'IBM Plex Mono',monospace;">
          picked <b style="color:${good ? '#4ADE80' : '#F87171'};">${escape(picked)}</b>
          · correct <b style="color:#4ADE80;">${escape(correctL)}</b>
          · ${fmtInt(a.ms)}ms
        </div>
      </div>
    </div>
  `;
}
